// This is the updated ClusteredBunkerPrices.tsx file with weather forecast SVG grid per port row
import React, { useState, useEffect, useRef } from "react";
import { OverlayView } from "@react-google-maps/api";
import type {
  TBunkerPrice,
  TCrudePrice,
  TCurrentVessel,
  TPort,
  TWeather,
} from "@/types";
import RequestForFirm from "./RequestForFirm";
import { formatUTC, formatUTC_DDMM } from "../utils/date";
import { getForecastColorsFromWeatherHours } from "../utilities/weatherUtils";
// Dialog logic has been migrated into shared PortWeatherForecast component
import PortWeatherForecast from "./PortWeatherForecast";
import ApiClient from "../services/fuelandmoreapi";
import { config } from "../config";
import { getWeatherForPortCached } from "../services/weatherService";

import "./ClusteredBunkerPrices.css"; // Import CSS for modal styling

interface ClusteredBunkerPricesProps {
  vessel: TCurrentVessel | null;
  fromPort: string;
  destinationPort: string;
  eta: string;
  bunkerMarkers: TPort[];
  prices: TBunkerPrice[];
  zoom: number;
  crudePrice: TCrudePrice | undefined;
  getPricesForPort: (unlocode: string) => TBunkerPrice[];
}

interface ClusteredBunkerPrice {
  bunkerMarkers: TPort[];
  prices: TBunkerPrice[];
  weather: Map<string, TWeather[]>;
}

// DailyForecastRow + related formatting now handled inside PortWeatherForecast

const ZOOM_THRESHOLD = 20;

function getClusterDistanceNM(zoom: number): number {
  console.log("Zoom:", zoom);
  if (zoom >= 18) return 5;
  if (zoom >= 16) return 10;
  if (zoom >= 14) return 20;
  if (zoom >= 12) return 40;
  if (zoom >= 10) return 60;
  if (zoom >= 8) return 80;
  if (zoom >= 6) return 400;
  if (zoom >= 4) return 1200;
  return 2000;
}

function nauticalMilesBetween(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 3440.1;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function clusterPorts(markers: TPort[], zoom: number): TPort[][] {
  const clusters: TPort[][] = [];
  const maxDistanceNM = getClusterDistanceNM(zoom);

  for (const port of markers) {
    let added = false;
    for (const cluster of clusters) {
      const ref = cluster[0];
      const distance = nauticalMilesBetween(
        port.latitude,
        port.longitude,
        ref.latitude,
        ref.longitude
      );
      if (distance < maxDistanceNM) {
        cluster.push(port);
        added = true;
        break;
      }
    }
    if (!added) clusters.push([port]);
  }

  return clusters;
}

// Removed createTableIcon in favour of interactive OverlayView DOM

// metric formatting now handled inside shared component

const ClusteredBunkerPrices: React.FC<ClusteredBunkerPricesProps> = (props) => {
  const {
    vessel,
    fromPort,
    destinationPort,
    eta,
    bunkerMarkers,
    prices,
    zoom,
    getPricesForPort,
  } = props;

  // Deduplicate bunkerMarkers by unlocode
  const allPorts: TPort[] = React.useMemo(() => {
    const seen = new Set<string>();
    const result: TPort[] = [];
    for (const marker of bunkerMarkers) {
      if (!seen.has(marker.unlocode)) {
        seen.add(marker.unlocode);
        result.push(marker);
      }
    }
    return result;
  }, [bunkerMarkers]);

  // Ports with at least one VLSFO price (used for display + weather fetch optimization)
  const displayPorts: TPort[] = React.useMemo(() => {
    return allPorts.filter((marker) =>
      getPricesForPort(marker.unlocode).some(
        (p: TBunkerPrice) => p.fuelGrade.toLowerCase() === "vlsfo"
      )
    );
  }, [allPorts, getPricesForPort]);
  const [selectedCluster, setSelectedCluster] =
    useState<ClusteredBunkerPrice | null>(null);
  const [forecastColorsMap, setForecastColorsMap] = useState<{
    [unlocode: string]: string[];
  }>({});
  const [weatherDataMap, setWeatherDataMap] = useState<{
    [unlocode: string]: TWeather[];
  }>({});
  const [featureFlags, setFeatureFlags] = useState<Record<string, any>>({});
  const apiClientRef = useRef<ApiClient | null>(null);
  const [weatherDialogPort, setWeatherDialogPort] = useState<TPort | null>(
    null
  );
  // Dedicated forecast dialog state now simplified to just selected port

  const apiUrl = config.apiBaseUrl;

  useEffect(() => {
    apiClientRef.current = new ApiClient(apiUrl || "");
  }, []);

  useEffect(() => {
    // fetch feature flags once
    (async () => {
      try {
        const flags = await apiClientRef.current!.getFeatureFlags();
        setFeatureFlags(flags || {});
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  useEffect(() => {
    // Get all unique unlocodes for ports that have at least one VLSFO price
    const vlsfoPorts = new Set<string>();
    displayPorts.forEach((marker: TPort) => {
      // Always normalize to lowercase for API and map keys
      vlsfoPorts.add(marker.unlocode.toLowerCase()); // only fetch weather for ports we display
    });

    const fetchWeatherForPorts = async () => {
      const unlocodesToFetch = Array.from(vlsfoPorts).filter(
        (unlocodeLC) =>
          !forecastColorsMap[unlocodeLC] || !weatherDataMap[unlocodeLC]
      );
      if (unlocodesToFetch.length === 0) return;
      // Fetch all weather in parallel
      unlocodesToFetch.forEach(async (unlocodeLC) => {
        const weatherData = await getWeatherForPortCached(unlocodeLC);
        const colors = getForecastColorsFromWeatherHours(
          weatherData,
          "swellHeight"
        );
        setForecastColorsMap((prev) => ({ ...prev, [unlocodeLC]: colors }));
        setWeatherDataMap((prev) => ({ ...prev, [unlocodeLC]: weatherData }));
      });
    };
    fetchWeatherForPorts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bunkerMarkers, getPricesForPort, displayPorts]);

  function openWeatherDialog(port: TPort) {
    setWeatherDialogPort(port);
  }

  function closeWeatherDialog() {
    setWeatherDialogPort(null);
  }

  const handleClusterClick = (cluster: ClusteredBunkerPrice) => {
    setSelectedCluster(cluster);
  };

  const closeModal = () => {
    setSelectedCluster(null);
  };

  const renderPortBlock = (port: TPort) => {
    // Normalize to lowercase for map lookups and API parity, but keep original for display
    const unlocode = port.unlocode.toLowerCase();
    const originalUnlocode = port.unlocode;
    const country = originalUnlocode.slice(0, 2).toUpperCase();
    const forecastColors = forecastColorsMap[unlocode] || [];
    const vlsfo = getPricesForPort(port.unlocode).filter(
      (p: TBunkerPrice) => p.fuelGrade.toLowerCase() === "vlsfo"
    );
    if (vlsfo.length === 0) return null; // safety check
    return (
      <div key={originalUnlocode} style={{ paddingTop: 4 }}>
        <div
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            fontWeight: 600,
            marginBottom: 2,
          }}
        >{`${port.name} (${country})`}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: 11 }}>Swell Height *:</span>
          <div
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              openWeatherDialog(port);
            }}
            title="Click for 10-day weather forecast"
            style={{ cursor: "pointer", display: "flex", gap: 2 }}
          >
            {forecastColors.map((c, i) => (
              <span
                key={i}
                style={{
                  width: 6,
                  height: 12,
                  background: c,
                  borderRadius: 2,
                  display: "inline-block",
                }}
              />
            ))}
          </div>
        </div>
        {vlsfo.map((p) => (
          <div
            key={p.timestamp.toString()}
            style={{
              display: "flex",
              fontFamily: "Inter, sans-serif",
              fontSize: 11,
              justifyContent: "space-between",
              marginTop: 2,
            }}
          >
            <span style={{ fontWeight: 500 }}>{p.fuelGrade.toUpperCase()}</span>
            <span>${p.price.toFixed(1)}</span>
            <span>
              {(() => {
                const ts = new Date(p.timestamp as any);
                const now = new Date();
                const isToday =
                  ts.getUTCFullYear() === now.getUTCFullYear() &&
                  ts.getUTCMonth() === now.getUTCMonth() &&
                  ts.getUTCDate() === now.getUTCDate();
                return isToday
                  ? formatUTC(p.timestamp, "time")
                  : formatUTC_DDMM(p.timestamp);
              })()}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const overlays: React.ReactNode[] = [];

  if (zoom < ZOOM_THRESHOLD) {
    const clustered = clusterPorts(allPorts, zoom); // cluster using ALL ports
    clustered.forEach((groupAll: TPort[], i: number) => {
      const avgLat =
        groupAll.reduce((sum, p) => sum + p.latitude, 0) / groupAll.length;
      const avgLng =
        groupAll.reduce((sum, p) => sum + p.longitude, 0) / groupAll.length;
      // Apply display filtering (VLSFO + 3 per country) only for rendering
      let portsToRender: TPort[] = groupAll.filter((p) =>
        displayPorts.some((d) => d.unlocode === p.unlocode)
      );
      const hiddenNoVlsfoCount = groupAll.filter(
        (p) => !displayPorts.some((d) => d.unlocode === p.unlocode)
      ).length;
      const totalVlsfoInGroup = groupAll.filter((p) =>
        displayPorts.some((d) => d.unlocode === p.unlocode)
      ).length;
      let excludedByCountryCount = 0;
      if (featureFlags.MAP_CLUSTER_FILTER_BY_COUNTRY) {
        const byCountry = new Map<string, TPort[]>();
        for (const port of portsToRender) {
          const cc = port.unlocode.slice(0, 2).toUpperCase();
          const list = byCountry.get(cc) ?? [];
          list.push(port);
          byCountry.set(cc, list);
        }
        const selected: TPort[] = [];
        byCountry.forEach((ports) => {
          if (ports.length <= 3) selected.push(...ports);
          else {
            const withMinPrice = ports.map((p) => {
              const vlsfoPrices = getPricesForPort(p.unlocode).filter(
                (bp: TBunkerPrice) => bp.fuelGrade.toLowerCase() === "vlsfo"
              );
              const minPrice =
                vlsfoPrices.length > 0
                  ? Math.min(...vlsfoPrices.map((bp) => bp.price))
                  : Number.POSITIVE_INFINITY;
              return { port: p, minPrice };
            });
            withMinPrice
              .sort((a, b) => a.minPrice - b.minPrice)
              .slice(0, 3)
              .forEach((x) => selected.push(x.port));
          }
        });
        portsToRender = selected;
        excludedByCountryCount = totalVlsfoInGroup - portsToRender.length;
      }
      // Skip cluster entirely if no VLSFO ports are present
      if (portsToRender.length === 0) {
        return; // continue to next cluster
      }
      overlays.push(
        <OverlayView
          key={`cluster-ov-${i}`}
          position={{ lat: avgLat, lng: avgLng }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            onClick={() =>
              handleClusterClick({
                // Provide ALL ports originally in this cluster prior to display filtering
                bunkerMarkers: groupAll,
                prices: prices.filter((p: TBunkerPrice) =>
                  groupAll.some(
                    (port: TPort) =>
                      port.unlocode.toLowerCase() === p.bunkerId.toLowerCase()
                  )
                ),
                weather: new Map(),
              })
            }
            style={{
              background: "rgba(255,255,255,0.85)",
              borderRadius: 8,
              padding: 8,
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              width: 220,
              cursor: "pointer",
            }}
          >
            {portsToRender.map(renderPortBlock)}
            {hiddenNoVlsfoCount > 0 && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 10,
                  opacity: 0.7,
                  lineHeight: 1.2,
                }}
              >
                {hiddenNoVlsfoCount} hidden port
                {hiddenNoVlsfoCount === 1 ? "" : "s"} (no VLSFO price)
              </div>
            )}
            {excludedByCountryCount > 0 && (
              <div
                style={{
                  marginTop: 4,
                  fontSize: 10,
                  opacity: 0.7,
                  lineHeight: 1.2,
                }}
              >
                {excludedByCountryCount} port
                {excludedByCountryCount === 1 ? "" : "s"} excluded (not in 3
                lowest prices per country)
              </div>
            )}
            <div
              style={{
                marginTop: 6,
                fontSize: 10,
                opacity: 0.7,
                lineHeight: 1.2,
              }}
            >
              * 10 day forecast from today &lt; 1m are{" "}
              <span style={{ color: "#0a7d34", fontWeight: 600 }}>green</span>;
              &gt; 1.5m are{" "}
              <span style={{ color: "#c62828", fontWeight: 600 }}>red</span>.
            </div>
          </div>
        </OverlayView>
      );
    });
  } else {
    displayPorts.forEach((marker: TPort) => {
      overlays.push(
        <OverlayView
          key={`port-ov-${marker.unlocode.toLowerCase()}`}
          position={{ lat: marker.latitude, lng: marker.longitude }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            onClick={() =>
              handleClusterClick({
                bunkerMarkers: [marker],
                prices: getPricesForPort(marker.unlocode),
                weather: new Map(),
              })
            }
            style={{
              background: "rgba(255,255,255,0.85)",
              borderRadius: 8,
              padding: 8,
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              width: 220,
              cursor: "pointer",
            }}
          >
            {renderPortBlock(marker)}
            <div
              style={{
                marginTop: 6,
                fontSize: 10,
                opacity: 0.7,
                lineHeight: 1.2,
              }}
            >
              Swell heights &lt; 1m are{" "}
              <span style={{ color: "#0a7d34", fontWeight: 600 }}>green</span>;
              &gt; 1.5m are{" "}
              <span style={{ color: "#c62828", fontWeight: 600 }}>red</span>.
            </div>
          </div>
        </OverlayView>
      );
    });
  }

  return (
    <>
      {overlays}
      {selectedCluster && (
        <RequestForFirm
          clusteredBunkerPrice={selectedCluster}
          vessel={vessel}
          fromPort={fromPort}
          toPort={destinationPort}
          toEta={eta}
          onCancel={closeModal}
        />
      )}
      <PortWeatherForecast
        port={weatherDialogPort}
        weather={
          weatherDialogPort
            ? weatherDataMap[weatherDialogPort.unlocode.toLowerCase()]
            : undefined
        }
        open={!!weatherDialogPort}
        onClose={closeWeatherDialog}
      />
    </>
  );
};

export default ClusteredBunkerPrices;
